import { PublicSeatIdentity } from '../components/PublicSeatIdentity';
import { GameSettings } from '../display/GameDisplay';
// Structure bricks (presentation-2 contract section 5.1): hud, panel, seat-mat, opponent-strip,
// log-feed and result-podium.

import type { BrickInstance, Material } from '../presentation/types';
import { useEffect, useRef, useState } from 'react';
import { useScene, seatOf, zonesOf } from '../scene/SceneContext';
import { materialBackground, materialInk, materialTint, shade, luminance } from '../theme/materials';
import { fieldOf } from './occupants';
import { targetProps, usePlay } from '../play/PlayController';
import { pickShowsFaces } from '../play/targets';
import { BrickHost } from './host';
import { usePresentation } from '../presentation/PresentationContext';
import { Slot } from '../components/ObjectCard';
import { ArtImage } from '../art/ArtImage';
import { artAsset } from '../art/assets';
import { objectPortrait } from '../art/portrait';
import { RichLogLine } from '../scene/RichLogLine';
import { useReplay, useViews } from '../scene/ViewContext';
import type { ChoiceView } from '../model/choices';

interface BrickProps { inst: BrickInstance; seat: string | null }

const SPEEDS: [number, string][] = [[0.5, '0.5×'], [1, '1×'], [2, '2×'], [0, 'Instant']];

export function Hud({ inst }: BrickProps) {
  const { table, seatColor, speed, setSpeed, headerPrompt, promptHost } = useScene();
  const show = (inst.props?.show as string[] | undefined) ?? ['seats', 'round'];
  const seatCounter = inst.props?.seatCounter as string | undefined;
  const counterOf = (seat: string) => (seatCounter ? table.counters.find(c => c.owner === seat && (c.key ?? c.id) === seatCounter) ?? null : null);
  const shared = table.counters.filter(c => c.owner === null);
  const publicKeys = inst.props?.publicCounters as string[] | undefined;
  const publicCounters = publicKeys ? publicKeys.flatMap(k => shared.filter(c => (c.key ?? c.id) === k)) : shared;
  const self = table.seats.find(s => s.isSelf);
  const piles = table.zones.filter(z => z.owner === null && (z.kind === 'stack' || z.kind === 'discard'));
  const selfKeys = (inst.props?.selfCounters as string[] | undefined) ?? [];
  const selfCounters = self ? selfKeys.flatMap(k => table.counters.filter(c => c.owner === self.id && (c.key ?? c.id) === k)) : [];
  const material = inst.props?.material as Material | undefined;
  const rawTint = inst.props?.tint as string | undefined;
  const tint = rawTint && /^#[0-9a-f]{6}$/i.test(rawTint) ? rawTint : undefined;
  const ink = material ? materialInk(material, tint) : null;
  // `layout: "stacked"` (compact shell): the turn bar is two lines, the step chips over the instruction, the decision buttons at the right.
  const stacked = inst.props?.layout === 'stacked';
  const askObject = typeof inst.props?.ask === 'string' ? table.objects.get(inst.props.ask) : undefined;
  const ask = askObject ? String(askObject.fields.text ?? askObject.label ?? '') : '';
  const surface = material && ink ? { background: materialBackground(material, tint), color: ink.ink, '--table-ink': ink.ink, '--table-ink-muted': ink.muted } as React.CSSProperties : undefined;
  return (
    <header className={`hud${inst.props?.wrap ? ' is-wrapped' : ''}${stacked ? ' is-stacked' : ''}`} style={surface} aria-label="Game status">
      <div className="hud-title-group"><h1 className="hud-title">{table.title}</h1><GameSettings pace={speed} setPace={setSpeed} /></div>
      {show.includes('seats') && (
        <ol className="hud-seats" aria-label="Players">
          {table.seats.map(s => (
            <li key={s.id} className={`hud-seat${s.active && !table.finished ? ' is-active' : ''}${s.isSelf ? ' is-self' : ''}`} data-motion-seat={s.id}
              style={{ '--seat-color': seatColor(s.id) } as React.CSSProperties}>
              <span className="seat-dot" aria-hidden="true" />
              <span className="hud-seat-name"><PublicSeatIdentity seat={s} />{s.isSelf ? ' (you)' : ''}</span>
              {s.active && !table.finished && !headerPrompt && <span className="hud-seat-turn">{s.isSelf ? 'Your turn' : 'Taking a turn'}</span>}
              {s.status && !headerPrompt && <span className="hud-seat-status">{s.status}</span>}
              {counterOf(s.id) && <span className="hud-seat-points" data-motion-counter={counterOf(s.id)!.id} title={counterOf(s.id)!.label}>{String(counterOf(s.id)!.value)}</span>}
            </li>
          ))}
        </ol>
      )}
      {show.includes('self-status') && self?.status && <span className="hud-pill">{self.status}</span>}
      {show.includes('steps') && <StepChips inst={inst} />}
      {stacked && ask && <p className="hud-ask" title={ask}>{ask}</p>}
      {headerPrompt && <div className="hud-prompt" ref={promptHost} />}
      {(show.includes('self-piles') || selfCounters.length > 0) && self && (
        <dl className="hud-counters hud-self" aria-label="Your piles and counters">
          {selfCounters.map(c => <div key={c.id} data-motion-counter={c.id} title={c.label}><dt>{shortCounter(c.label)}</dt><dd>{String(c.value)}</dd></div>)}
          {show.includes('self-piles') && table.zones.filter(z => z.owner === self.id && (z.kind === 'stack' || z.kind === 'discard')).map(z => (
            <div key={z.id} data-motion-zone={z.id} title={z.label}><dt>{shortPile(z.label.replace(/ of .*$/, ''))}</dt><dd>{z.count ?? z.objects.length}</dd></div>
          ))}
        </dl>
      )}
      {show.includes('piles') && piles.length > 0 && (
        <dl className="hud-counters hud-piles">
          {piles.map(z => <div key={z.id} data-motion-zone={z.id} title={z.label}><dt>{headerPrompt ? shortPile(z.label) : z.label}</dt><dd>{z.count ?? z.objects.length}</dd></div>)}
        </dl>
      )}
      {(show.includes('counters:public') || publicKeys) && publicCounters.length > 0 && (
        <dl className="hud-counters">
          {publicCounters.map(c => <div key={c.id} data-motion-counter={c.id}><dt>{c.label}</dt><dd>{String(c.value)}</dd></div>)}
        </dl>
      )}
      {show.includes('round') && <p className={`hud-status${inst.props?.statusWrap ? ' is-wrapped' : ''}`} aria-live="polite">{table.status}</p>}
      {!inst.props?.hideSpeed && <div className="hud-speed" role="group" aria-label="Opponent animation speed">
        {SPEEDS.map(([value, label]) => (
          <button key={label} type="button" className={`hud-speed-option${speed === value ? ' is-on' : ''}`} aria-pressed={speed === value} onClick={() => setSpeed(value)}>{label}</button>
        ))}
      </div>}
    </header>
  );
}

/**
 * Turn-step chips (`hud.steps`, `stepCounter`): steps are `path:Label`; the counter's value names the
 * current one. The first path's steps always show, then the current path's; the current step is gold,
 * earlier ones read as done. Seeded by Thunderstone (Choose; Village abilities, Buy, Level up; or
 * Prepare, Battle, Spoils).
 */
function StepChips({ inst }: { inst: BrickInstance }) {
  const { table, seatColor } = useScene();
  // `stepSeats` (a counter key whose per-seat values are `done`, `active` or `waiting`) expands the step `stepSeatsAt`
  // into one chip per seat, with a check once that seat has finished it.
  const seatKey = inst.props?.stepSeats as string | undefined;
  const seatStep = inst.props?.stepSeatsAt as string | undefined;
  const seatChips = seatKey ? table.seats.flatMap(s => { const c = table.counters.find(x => x.owner === s.id && (x.key ?? x.id) === seatKey); return c ? [{ seat: s, state: String(c.value) }] : []; }) : [];
  const steps = ((inst.props?.steps as string[] | undefined) ?? []).map(s => { const i = s.indexOf(':'); return { key: s, path: i > 0 ? s.slice(0, i) : '', label: i > 0 ? s.slice(i + 1) : s }; });
  const counterId = (inst.props?.stepCounter as string | undefined) ?? 'step';
  const value = table.counters.find(c => c.id === counterId)?.value;
  // A row of chips that is wider than its room (a phone) scrolls its current chip into view.
  const list = useRef<HTMLOListElement>(null);
  useEffect(() => {
    const row = list.current, now = row?.querySelector<HTMLElement>('.is-current');
    if (!row || !now || row.scrollWidth <= row.clientWidth) return;
    if (now.offsetLeft < row.scrollLeft) row.scrollLeft = Math.max(0, now.offsetLeft - 4);
    else if (now.offsetLeft + now.offsetWidth > row.scrollLeft + row.clientWidth) row.scrollLeft = now.offsetLeft + now.offsetWidth - row.clientWidth + 4;
  }, [value]);
  if (!steps.length || table.finished) return null;
  const current = steps.find(s => s.key === value) ?? null;
  const first = steps[0]!.path;
  const shown = steps.filter(s => s.path === first || (current && s.path === current.path));
  const at = current ? shown.indexOf(current) : -1;
  return (
    <ol ref={list} className="hud-steps" aria-label="Turn steps">
      {shown.map((s, i) => s.key === seatStep && seatChips.length > 1 ? (
        <li key={s.key} className={`hud-step has-seats${i === at ? ' is-current' : i < at ? ' is-done' : ''}`} {...(i === at ? { 'aria-current': 'step' as const } : {})}>
          <span className="hud-step-label">{s.label}</span>
          {seatChips.map(({ seat, state }) => (
            <span key={seat.id} className={`hud-step-seat is-${state}`} style={{ '--seat-color': seatColor(seat.id) } as React.CSSProperties} title={`${seat.label}: ${state === 'done' ? 'finished' : state === 'active' ? 'taking their turn' : 'waiting'}`}>
              {state === 'done' && <span aria-hidden="true">✓ </span>}{seat.label}{seat.isSelf ? ' (you)' : ''}<span className="sr-only">{state === 'done' ? ', finished' : state === 'active' ? ', taking their turn' : ', waiting'}</span>
            </span>
          ))}
        </li>
      ) : (
        <li key={s.key} className={`hud-step${i === at ? ' is-current' : i < at ? ' is-done' : ''}`} {...(i === at ? { 'aria-current': 'step' as const } : {})}>{s.label}</li>
      ))}
    </ol>
  );
}

/** "Experience" reads "XP" in the header. */
function shortCounter(label: string): string {
  return /^experience$/i.test(label) ? 'XP' : label;
}

/** "Draw pile" reads "Draw", "Discard pile" reads "Discard": the header has little room. */
function shortPile(label: string): string {
  return label.replace(/\s+pile$/i, '');
}

/** A titled surface of a material that holds other bricks (the parchment centre of a table). */
export function Panel({ inst, seat }: BrickProps) {
  const play = usePlay();
  const details = useRef<HTMLDetailsElement>(null);
  const [expanded, setExpanded] = useState(inst.props?.defaultOpen !== false);
  const litKey = (play?.lit ?? []).join('|');
  useEffect(() => {
    if (details.current?.querySelector('.is-selectable, .is-option, .is-selected')) setExpanded(true);
  }, [play?.stepKey, litKey]);
  const material = ((inst.props?.material as Material | undefined) ?? 'parchment');
  const slots = (inst.props?.slots as BrickInstance[] | undefined) ?? [];
  const label = inst.props?.label as string | undefined;
  const rawTint = inst.props?.tint as string | undefined;
  const tint = rawTint && /^#[0-9a-f]{6}$/i.test(rawTint) ? rawTint : undefined;
  const ink = materialInk(material, tint);
  const surfaceTint = materialTint(material, tint);
  const panelTint = shade(surfaceTint, luminance(surfaceTint) < 0.2 ? -0.18 : 0.12);
  const background = inst.props?.background as string | undefined;
  return (
    <section className={`brick-panel brick-panel-${material} brick-panel-${(inst.props?.direction as string | undefined) ?? 'column'}${inst.props?.fill ? ' is-fill' : ''}${inst.props?.align === 'center' ? ' is-centered' : ''}${inst.props?.compact ? ' is-compact' : ''}${inst.props?.hideEmpty ? ' is-hide-empty' : ''}${background ? ' has-panel-art' : ''}`} aria-label={label}
      style={{ background: materialBackground(material, tint), '--table-ink': ink.ink, '--table-ink-muted': ink.muted, '--table-panel': panelTint, color: ink.ink } as React.CSSProperties}>
      {background && <ArtImage source={background} className="panel-background" />}
      {label && !inst.props?.collapsible && <h2 className="brick-panel-title">{label}</h2>}
      {inst.props?.collapsible ? <details ref={details} open={expanded} onToggle={e => setExpanded(e.currentTarget.open)} className="panel-disclosure">
        <summary>{label ?? 'Details'}</summary>
        <div className="brick-panel-slots">{slots.map((child, i) => <div key={i} className={`brick-panel-slot brick-panel-slot-${child.brick}`}><BrickHost inst={child} seat={seat} /></div>)}</div>
      </details> :
      <div className="brick-panel-slots">
        {slots.map((child, i) => <div key={i} className={`brick-panel-slot brick-panel-slot-${child.brick}`}><BrickHost inst={child} seat={seat} /></div>)}
      </div>}
    </section>
  );
}

export function SeatMat({ inst, seat }: BrickProps) {
  const { table, seatColor } = useScene();
  const play = usePlay();
  const id = seatOf(inst, seat, table.selfSeat);
  const s = table.seats.find(x => x.id === id);
  if (!s) return null;
  const slots = (inst.props?.slots as BrickInstance[] | undefined) ?? [];
  const anchor = `mat:${s.id}`;
  const t = targetProps(play, anchor, `${s.label}${s.isSelf ? ', your area' : ''}`, `seat-mat${s.active && !table.finished ? ' is-active' : ''}${inst.props?.compact ? ' is-compact' : ''}`);
  const st = play?.stateOf(anchor);
  return (
    <section {...t} role={st?.selectable ? 'button' : 'region'} tabIndex={st?.selectable ? 0 : -1} aria-label={s.isSelf ? 'Your area' : s.label}
      data-motion-seat={s.id} {...(s.isSelf && !inst.props?.compact ? { 'data-self-mat': '' } : {})} style={{ '--seat-color': seatColor(s.id) } as React.CSSProperties}>
      <header className={`mat-head${inst.props?.header === false ? ' is-hidden' : ''}`}>
        <span className="seat-dot" aria-hidden="true" />
        <h2 className="mat-name">{(inst.props?.label as string | undefined) ?? <><PublicSeatIdentity seat={s} />{s.isSelf ? ' (you)' : ''}</>}</h2>
        {s.active && !table.finished && <span className="mat-turn">{s.isSelf ? 'Your turn' : 'Taking a turn'}</span>}
        {s.status && <span className="mat-status">{s.status}</span>}
        {st?.button && <span className="mat-button" aria-hidden="true">{st.button}</span>}
      </header>
      <div className="mat-slots">
        {slots.map((child, i) => <div key={i} className={`mat-slot mat-slot-${child.brick}`}><BrickHost inst={child} seat={s.id} /></div>)}
      </div>
    </section>
  );
}

export function OpponentStrip({ inst, seat }: BrickProps) {
  const { table, seatColor, lastAction, presentation } = useScene();
  const play = usePlay();
  const api = usePresentation();
  const views = useViews();
  const replay = useReplay();
  const id = seatOf(inst, seat, table.selfSeat);
  const s = table.seats.find(x => x.id === id);
  const stripRef = useRef<HTMLElement>(null);
  const active = !!s?.active && !table.finished;
  const replayHighlighted = !!s && !!replay?.stop?.frame.events.some(event => event.message.includes(s.label));
  useEffect(() => {
    if (active) stripRef.current?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [active]);
  if (!s) return null;
  const show = (inst.props?.show as string[] | undefined) ?? ['counters', 'zones', 'last-action'];
  const keys = inst.props?.counterKeys as string[] | undefined;
  const counters = table.counters.filter(c => c.owner === s.id && (!keys || keys.includes(c.key ?? c.id)));
  const counterHelp = new Map(((inst.props?.counterHelp as string[] | undefined) ?? []).map(value => { const divider = value.indexOf('|'); return [value.slice(0, divider), value.slice(divider + 1)] as const; }));
  const subtitleKey = inst.props?.subtitleCounter as string | undefined;
  const subtitle = subtitleKey ? table.counters.find(c => c.owner === s.id && (c.key ?? c.id) === subtitleKey)?.value : null;
  const portraits = (inst.props?.portraits as string[] | undefined) ?? [];
  const portraitId = portraits.find(value => value.startsWith(`${s.id}|`))?.slice(s.id.length + 1);
  const portraitFrom = inst.props?.portraitFrom as string | undefined;
  // A portrait taken from the art of the seat's own observed object (a dealt hero card): no per-seat list can name it ahead of time.
  const portraitObject = portraitFrom ? zonesOf(portraitFrom, table, s.id).flatMap(z => z.objects).find(o => !o.faceDown) ?? null : null;
  const portraitSize = inst.props?.portraitSize as number | undefined;
  const portrait = portraitObject ? objectPortrait(presentation, portraitObject, (inst.props?.portraitSlot as string | undefined) ?? 'portrait', Math.max(80, (portraitSize ?? 22) * 2))
    : portraitId && presentation ? artAsset(presentation, portraitId.slice(6), 80) : null;
  // Hidden zones always list their counts here (the scene places them on this strip); visible ones only with 'zones'.
  const zoneKinds = inst.props?.zoneKinds as string[] | undefined;
  const zones = table.zones.filter(z => z.owner === s.id && z.kind !== 'board' && (!zoneKinds || zoneKinds.includes(z.kind)) && ((z.visibility !== 'public' && z.objects.length === 0) || show.includes('zones')));
  // 'Player 2 hand' and 'Hand of Player 2' both read 'Hand' on that player's own strip.
  const zoneLabel = (label: string) => {
    let t = label.toLowerCase().startsWith(s.label.toLowerCase() + ' ') ? label.slice(s.label.length + 1) : label;
    if (t.toLowerCase().endsWith(' of ' + s.label.toLowerCase())) t = t.slice(0, t.length - s.label.length - 4);
    return t.charAt(0).toUpperCase() + t.slice(1);
  };
  const chipsFrom = inst.props?.chipsFrom as string | undefined;
  const chipZone = chipsFrom ? zonesOf(chipsFrom, table, s.id)[0] ?? null : null;
  const chipField = (inst.props?.chipField as string | undefined) ?? 'color';
  const chipMap = inst.props?.chipMap ? presentation.theme.maps?.[inst.props.chipMap as string] : undefined;
  const anchor = `mat:${s.id}`;
  const compact = inst.props?.layout === 'compact';
  const viewTab = inst.props?.viewTab as string | undefined;
  const tab = viewTab ? views?.tabs.find(x => x.id === viewTab && (x.seat === null || x.seat === s.id)) ?? null : null;
  const t = targetProps(play, anchor, s.label, `opponent-strip${compact ? ' is-compact' : ''}${s.active && !table.finished ? ' is-active' : ''}${replayHighlighted ? ' is-replay-highlight' : ''}`);
  const st = play?.stateOf(anchor);
  return (
    <section {...t} ref={stripRef} role={st?.selectable ? 'button' : 'region'} tabIndex={st?.selectable ? 0 : -1} aria-label={s.label} data-motion-seat={s.id}
      style={{ '--seat-color': seatColor(s.id) } as React.CSSProperties}>
      <header className="strip-head">
        {portrait ? <span className="strip-portrait" style={portraitSize ? { width: portraitSize, height: portraitSize } : undefined}><Slot value={'kind' in portrait ? portrait : { kind: 'image', url: portrait.url }} /></span> : <span className="seat-dot" aria-hidden="true" />}
        <h2 className="strip-name"><PublicSeatIdentity seat={s} /></h2>
        {active && <span className="strip-action" role="status" aria-label={s.status ?? 'Taking a turn'} title={s.status ?? 'Taking a turn'}>◷</span>}
        {s.active && !table.finished && <span className="strip-turn">Taking a turn</span>}
      </header>
      {subtitle !== null && subtitle !== undefined && <p className="strip-subtitle">{String(subtitle)}</p>}
      {s.status && <p className="strip-status">{s.status}</p>}
      {compact && show.includes('counters') && counters.length > 0 && (
        <ul className="strip-mini" aria-label={`${s.label} counters`}>
          {counters.map(c => {
            const icon = c.key ? api.icon(c.key) : null;
            return (
              <li key={c.id} className="strip-mini-chip" data-motion-counter={c.id} title={counterHelp.get(c.key ?? c.id) ?? c.label} aria-label={`${c.label}: ${String(c.value)}${counterHelp.has(c.key ?? c.id) ? `. ${counterHelp.get(c.key ?? c.id)}` : ''}`}>
                {icon ? <span className="strip-icon" aria-hidden="true"><Slot value={icon} /></span> : <span className="strip-mini-key" aria-hidden="true">{c.label}</span>}
                <b aria-hidden="true" className={typeof c.value === 'string' ? 'is-text' : undefined}>{String(c.value)}</b>
              </li>
            );
          })}
        </ul>
      )}
      {!compact && show.includes('counters') && counters.length > 0 && (
        <dl className="strip-counters">
          {counters.map(c => {
            const icon = c.key ? api.icon(c.key) : null;
            return (
                <div key={c.id} data-motion-counter={c.id} title={counterHelp.get(c.key ?? c.id) ?? c.label} aria-label={`${c.label}: ${String(c.value)}${counterHelp.has(c.key ?? c.id) ? `. ${counterHelp.get(c.key ?? c.id)}` : ''}`}>
                <dt>{icon && <span className="strip-icon" aria-hidden="true"><Slot value={icon} /></span>}{c.label}</dt>
                <dd>{String(c.value)}</dd>
              </div>
            );
          })}
        </dl>
      )}
      {zones.length > 0 && (
        <ul className="strip-zones">
          {zones.map(z => <li key={z.id} data-motion-zone={z.id}><span>{zoneLabel(z.label)}</span><b>{z.count ?? z.objects.length}</b></li>)}
        </ul>
      )}
      {chipZone && (
        <div className="strip-chips" role="list" aria-label={chipZone.label} data-motion-zone={chipZone.id}>
          {chipZone.objects.filter(o => !o.stack).map(o => {
            const value = String(fieldOf(o, chipField) ?? '');
            return (
              <button key={o.id} type="button" role="listitem" className="strip-chip" data-motion-id={o.id} aria-label={o.label} title={o.title}
                style={{ '--chip-color': chipMap?.[value] ?? 'var(--table-ink-muted)' } as React.CSSProperties} onClick={e => { e.stopPropagation(); play?.inspect(o.id); }} />
            );
          })}
          {chipZone.objects.length === 0 && <span className="strip-chips-empty">No cards yet</span>}
        </div>
      )}
      {show.includes('last-action') && lastAction.get(s.id) && <p className="strip-last">{lastAction.get(s.id)}</p>}
      {tab && views && (
        <button type="button" className={`strip-view${views.current === tab.key ? ' is-on' : ''}`} aria-pressed={views.current === tab.key}
          onClick={e => { e.stopPropagation(); views.select(tab.key); }}>{(inst.props?.viewLabel as string | undefined) ?? `View ${tab.label}`}</button>
      )}
    </section>
  );
}

export function LogFeed({ inst }: BrickProps) {
  const { log } = useScene();
  const replay = useReplay();
  const limit = (inst.props?.limit as number | undefined) ?? 30;
  const entries = log.slice(-limit).reverse();
  const on = replay?.stop?.frame.revision ?? null;
  if (inst.props?.replay && replay) {
    return (
      <section className="log-feed is-replay" aria-label={(inst.props?.label as string | undefined) ?? 'Recent moves'}>
        <h2 className="brick-title">{(inst.props?.label as string | undefined) ?? 'Recent moves'}</h2>
        {entries.length === 0 ? <p className="brick-empty">Moves will appear here.</p> : (
          <ol className="log-feed-list">
            {entries.map(e => (
              <li key={e.id}>
                <button type="button" className={`log-entry${on === e.revision ? ' is-on' : ''}`} aria-pressed={on === e.revision}
                  title="Replay this move" onClick={() => (on === e.revision ? replay.exit() : replay.enter(e.revision))}><RichLogLine message={e.message} /></button>
              </li>
            ))}
          </ol>
        )}
      </section>
    );
  }
  return (
    <section className="log-feed" aria-label={(inst.props?.label as string | undefined) ?? 'Recent moves'}>
      <h2 className="brick-title">{(inst.props?.label as string | undefined) ?? 'Recent moves'}</h2>
      {entries.length === 0 ? <p className="brick-empty">Moves will appear here.</p> : (
        <ol className="log-feed-list">{entries.map(e => <li key={e.id}><RichLogLine message={e.message} /></li>)}</ol>
      )}
    </section>
  );
}

/**
 * Large labelled doors for moves that name no object (`tiles`: `id|Title|Description`). Each tile is a
 * `tile:<id>` anchor that targets.json routes choices to. Seeded by Thunderstone's Village, Dungeon and Rest.
 */
export function ActionTiles({ inst }: BrickProps) {
  const play = usePlay();
  const replay = useReplay();
  const { presentation } = useScene();
  const replayKinds = new Set(replay?.stop?.frame.events.map(event => event.kind) ?? []);
  const tiles = ((inst.props?.tiles as string[] | undefined) ?? []).map(t => { const [id, title, text] = t.split('|'); return { id: id!, title: title ?? id!, text: text ?? '' }; });
  const images = new Map(((inst.props?.images as string[] | undefined) ?? []).map(s => { const [id, source] = s.split('|'); return [id!, source!] as const; }));
  const style = inst.props?.style as string | undefined;
  const arch = style !== 'plain' && style !== 'list';
  const label = inst.props?.label as string | undefined;
  if (style === 'list') return <ActionList tiles={tiles} label={label} except={(inst.props?.exceptGroups as string[] | undefined) ?? []} />;
  return (
    <section className={`action-tiles${arch ? ' is-arch' : ''}`} aria-label={label ?? 'Choose a move'}>
      {label && <h2 className="brick-title">{label}</h2>}
      <div className="action-tiles-row">
        {tiles.map(t => {
          const anchor = `tile:${t.id}`;
          const st = play?.stateOf(anchor);
          const replayHighlighted = replayKinds.size > 0 && presentation.targets.targets.some(rule => rule.anchor === anchor && !!rule.match.kind && replayKinds.has(rule.match.kind));
          if (inst.props?.hideUnavailable && !st?.selectable && !st?.selected && !st?.option && !replayHighlighted) return null;
          const p = targetProps(play, anchor, t.title, `action-tile action-tile-${t.id}${images.has(t.id) ? ' has-scene-art' : ''}${st?.selectable || st?.selected ? '' : ' is-idle'}${replayHighlighted ? ' is-replay-highlight' : ''}`);
          return (
            <div key={t.id} {...p} aria-disabled={!st?.selectable && !st?.selected ? true : undefined} title={t.text || t.title} aria-description={t.text || undefined}>
              {images.has(t.id) && <ArtImage source={images.get(t.id)} />}
              <span className="action-tile-door" aria-hidden="true" />
              <span className="action-tile-title">{t.title}</span>
              {t.text && <span className="action-tile-text">{t.text}</span>}
            </div>
          );
        })}
      </div>
    </section>
  );
}

/**
 * `action-tiles` style `list`: what the viewer can do now, one row per available choice, written from the descriptors
 * (the choice label, its prompt and the objects its first pick offers). Tile rows come first, in tile order; the choices of
 * an open decision (payment, defense, a response window) follow, so each option says what it does. A row starts the same
 * draft a click on its target does. `exceptGroups` leaves choice groups out (the plays that are made from the hand).
 */
function ActionList({ tiles, label, except }: { tiles: { id: string; title: string; text: string }[]; label: string | undefined; except: string[] }) {
  const play = usePlay();
  const { table } = useScene();
  const hidden = new Set(except);
  const tileIndex = (anchor: string) => tiles.findIndex(t => `tile:${t.id}` === anchor);
  const rows = (play?.choices ?? []).flatMap(choice => {
    const anchors = play?.targets.byChoice.get(choice.id);
    if (!anchors?.length || hidden.has(choice.group)) return [];
    // Prefer the tile the choice is routed to, else any anchor it has.
    const anchor = anchors.find(a => tileIndex(a) >= 0) ?? anchors[0]!;
    const found = play?.targets.anchors.get(anchor)?.find(e => e.choiceId === choice.id);
    if (!found) return [];
    // A pre-answered option object (a hand card of a payment) must not decide the pick: the row starts the choice itself.
    const { answer: _answer, detail: _detail, ...entry } = found;
    return [{ tile: tileIndex(anchor), anchor, entry, choice }];
  });
  rows.sort((x, y) => (x.tile < 0 ? 999 : x.tile) - (y.tile < 0 ? 999 : y.tile));
  const targetsOf = (choice: ChoiceView) => {
    // The cards of this pick are drawn as faces for the viewer alone: the prompt already points at them.
    if (pickShowsFaces(choice, table)) return null;
    const step = choice.steps[0];
    return step?.type === 'pick' && !step.expand && step.options.length > 1 && step.options.length <= 4 ? step.options.map(o => o.label).join(' or ') : null;
  };
  return (
    <section className="action-list" aria-label={label ?? 'Your actions'}>
      {label && <h2 className="brick-title">{label}</h2>}
      {rows.length === 0 && <p className="action-list-empty">{play?.isMyTurn ? 'Nothing to do with the table right now.' : 'Waiting for your turn.'}</p>}
      <ol className="action-list-rows">
        {rows.map(({ tile, anchor, entry, choice }, i) => {
          const picked = play?.active?.choice.id === choice.id;
          const detail = [choice.prompt, targetsOf(choice)].filter(Boolean).join(' \u00b7 ');
          const key = tile >= 0 ? tiles[tile]!.id : 'decision';
          return (
            <li key={choice.id}>
              <button type="button" className={`action-row action-row-${key}${picked ? ' is-selected' : ''}`} data-anchor={anchor} data-choice={choice.id}
                disabled={!!play?.pending} onClick={() => play?.chooseEntry(anchor, entry)}>
                <span className="action-row-n" aria-hidden="true">{i + 1}</span>
                <span className="action-row-text"><b>{choice.label}</b>{detail && <span>{detail}</span>}</span>
              </button>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

export function ResultPodium({ inst }: BrickProps) {
  const { table, seatColor, seatLabel } = useScene();
  const result = table.result;
  const [compact, setCompact] = useState(false);
  const outcome = inst.props?.outcome==='team' && result
    ? result.winners.length===0?'defeat':table.seats.length>0&&table.seats.every(s=>result.winners.includes(s.id))?'victory':'neutral'
    : !result || !table.selfSeat || result.winners.length === 0 ? 'neutral' : result.winners.includes(table.selfSeat) ? 'victory' : 'defeat';
  const artwork = inst.props?.[`${outcome}Art`] as string | undefined;
  const title = inst.props?.[`${outcome}Title`] as string | undefined;
  const text = inst.props?.[`${outcome}Text`] as string | undefined;
  const showScores = inst.props?.showScores !== false && (result?.standings.some(s=>s.score!==undefined) ?? false);
  const scoreLabel = inst.props?.scoreLabel as string | undefined;
  const showDetails = result?.standings.some(s => !!s.detail) ?? false;
  if (!table.finished) return null;
  const standings = result ? [...result.standings].sort((a, b) =>
    b.score !== undefined && a.score !== undefined ? b.score - a.score || a.rank - b.rank : a.rank - b.rank) : [];
  return (
    <section className={`result-podium${artwork && !compact ? ' has-scene-art' : ''}${scoreLabel ? ' is-scoreboard' : ''}`} data-outcome={outcome} role="status" aria-label="Game over">
      {artwork && !compact && <ArtImage source={artwork} className="result-background" />}
      <h2 className="podium-title">{title ?? 'Game over'}</h2>
      {title && <p className="podium-over">Game over</p>}
      {text && <p className="podium-flavor">{text}</p>}
      {result ? (
        <>
          <p className="podium-winners">{result.winners.length === 0 ? 'No winner.' : result.winners.length === 1 ? `${seatLabel(result.winners[0]!)} wins.` : `Winners: ${result.winners.map(seatLabel).join(', ')}.`}</p>
          <table className="podium-table">
            <thead><tr><th scope="col">Rank</th><th scope="col">Player</th>{showScores && <th scope="col">{scoreLabel ?? 'Score'}</th>}{showDetails && <th scope="col">Detail</th>}</tr></thead>
            <tbody>
              {standings.map(s => (
                <tr key={s.seat} className={result.winners.includes(s.seat) ? 'is-winner' : undefined} style={{ '--seat-color': seatColor(s.seat) } as React.CSSProperties}>
                  <td>{s.rank}</td><td><span className="seat-dot" aria-hidden="true" />{seatLabel(s.seat)}</td>{showScores && <td className="podium-score">{s.score}</td>}{showDetails && <td>{s.detail ?? ''}</td>}
                </tr>
              ))}
            </tbody>
          </table>
          <p className="podium-reason">{(inst.props?.reasonText as string | undefined) ?? result.reason}</p>
        </>
      ) : <p className="podium-winners">{table.status}</p>}
      {artwork && <button type="button" className="bar-button" onClick={() => setCompact(v => !v)}>{compact ? 'Show adventure result' : 'Compact results'}</button>}
    </section>
  );
}
